import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '../store/AppContext'
import { Avatar } from './Avatar'
import { Pill } from './Pill'
import { Sheet } from './Sheet'
import { CoverPanel } from './CoverPanel'
import { typeMeta } from '../lib/eventMeta'
import { needsCover, wasCovered } from '../lib/cover'
import type { EventStatus, EventType, ScheduleEvent } from '../types'

const statusMeta: Record<EventStatus, { label: string; style: string }> = {
  pending: { label: 'Awaiting response', style: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Accepted', style: 'bg-emerald-100 text-emerald-700' },
  declined: { label: 'Declined', style: 'bg-rose-100 text-rose-700' },
}

export function EventCard({ event, showDate = false }: { event: ScheduleEvent; showDate?: boolean }) {
  const { currentUser, allUsers, respondToEvent, can, coverRequestsForEvent } = useApp()
  const navigate = useNavigate()
  const [showEdit, setShowEdit] = useState(false)
  const [showFindCover, setShowFindCover] = useState(false)
  const [showWithdraw, setShowWithdraw] = useState(false)
  const meta = typeMeta[event.type]
  const showAdultCalendarDetails = (currentUser.id === 'u-alex' || currentUser.id === 'u-mark') && !!event.calendarCategory
  const timeLabel = showAdultCalendarDetails && event.calendarTiming === 'all-day' ? 'All day' : showAdultCalendarDetails && event.calendarTiming === 'no-time' ? 'No time' : `${event.startTime} – ${event.endTime}`
  const myAssignee = event.assignees.find((a) => a.userId === currentUser.id)
  const needsResponse = myAssignee?.status === 'pending'
  const canEdit = currentUser.id === event.createdByUserId
  const dateLabel = showDate
    ? new Date(event.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
    : null

  // Needs Cover: same shift record, shown differently depending on who is
  // looking. The worker sees a way out; the participant sees who can step in.
  const shiftNeedsCover = needsCover(event)
  const shiftWasCovered = wasCovered(event)
  const canArrangeCover = can('manage_cover')
  const canWithdraw = event.type === 'shift' && myAssignee?.status === 'accepted' && can('cover_shifts')
  const withdrawnBy = allUsers.find((u) => u.id === event.cover?.originalAssigneeUserId)
  const openRequests = shiftNeedsCover
    ? coverRequestsForEvent(event.id).filter((r) => r.status === 'pending')
    : []

  return (
    <div
      role={canEdit ? 'button' : undefined}
      onClick={() => canEdit && setShowEdit(true)}
      className={`rounded-2xl border p-3.5 shadow-card ${
        shiftNeedsCover ? 'border-rose-300 bg-rose-50' : `${meta.bg} ${meta.border}`
      } ${canEdit ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start gap-2">
        <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm ${shiftNeedsCover ? 'bg-rose-100' : meta.iconBg}`}>
          {shiftNeedsCover ? '⚠️' : showAdultCalendarDetails && event.calendarSymbol ? event.calendarSymbol : meta.icon}
        </span>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="text-sm font-bold text-slate-900">{event.title}</p>
            {showAdultCalendarDetails && <Pill className="bg-white/80 text-slate-700">{event.calendarCategory}</Pill>}
            {shiftNeedsCover && <Pill className="bg-rose-600 text-white">Needs Cover</Pill>}
          </div>
          <p className="text-xs text-slate-500">
            {dateLabel ? `${dateLabel} · ` : ''}
            {timeLabel}
          </p>
          {event.notes && <p className="mt-1 text-xs text-slate-600">{event.notes}</p>}

          {shiftNeedsCover && (
            <div className="mt-2 rounded-xl border border-rose-200 bg-white/70 p-2.5">
              <p className="text-xs font-semibold text-rose-800">
                {withdrawnBy ? `${withdrawnBy.name} can no longer do this shift.` : 'This shift needs a support worker.'}
              </p>
              {event.cover?.reason && <p className="mt-0.5 text-xs text-rose-700">“{event.cover.reason}”</p>}
              <p className="mt-0.5 text-[11px] text-rose-700">
                {event.assignees.length > 0
                  ? 'This shift still needs another support worker.'
                  : canArrangeCover
                    ? 'Nobody is assigned yet — you choose who covers it.'
                    : 'Nobody is assigned yet.'}
              </p>
              {openRequests.length > 0 && (
                <p className="mt-1 text-[11px] font-semibold text-amber-700">
                  Cover request sent to {openRequests.length}{' '}
                  {openRequests.length === 1 ? 'person' : 'people'} · waiting for a reply
                </p>
              )}
            </div>
          )}

          <div className="mt-2 flex flex-col gap-1.5">
            {event.assignees.length === 0 && !shiftNeedsCover && !showAdultCalendarDetails && (
              <Pill className="w-fit bg-slate-100 text-slate-500">Unassigned</Pill>
            )}
            {event.assignees.map((a) => {
              const person = allUsers.find((u) => u.id === a.userId)
              if (!person) return null
              const isReplacement = shiftWasCovered && event.cover?.filledByUserId === a.userId
              return (
                <div key={a.userId} className="flex flex-wrap items-center gap-1.5">
                  <Avatar user={person} size={20} />
                  <span className="text-xs font-medium text-slate-600">{person.name}</span>
                  {a.status !== 'accepted' && (
                    <Pill className={statusMeta[a.status].style}>{statusMeta[a.status].label}</Pill>
                  )}
                  {isReplacement && <Pill className="bg-emerald-100 text-emerald-700">Covering</Pill>}
                </div>
              )
            })}
          </div>

          {shiftWasCovered && withdrawnBy && (
            <p className="mt-1 text-[11px] text-slate-500">
              Originally {withdrawnBy.name} — put out for cover and now covered.
            </p>
          )}

          {event.sourceGroupId && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/groups/${event.sourceGroupId}`)
              }}
              className="mt-2 text-[11px] font-semibold text-brand-600"
            >
              💬 View group chat
            </button>
          )}

          {canEdit && <p className="mt-2 text-[11px] font-medium text-slate-400">Tap to edit</p>}

          {shiftNeedsCover && canArrangeCover && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowFindCover(true)
              }}
              className="mt-3 w-full rounded-xl bg-rose-600 py-2.5 text-xs font-bold text-white"
            >
              Find cover from my team
            </button>
          )}

          {canWithdraw && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowWithdraw(true)
              }}
              className="mt-3 w-full rounded-xl border border-slate-300 bg-white py-2 text-xs font-bold text-slate-600"
            >
              I can't do this shift
            </button>
          )}

          {needsResponse && (
            <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => respondToEvent(event.id, 'declined')}
                className="flex-1 rounded-xl border border-rose-200 bg-white py-2 text-xs font-bold text-rose-600"
              >
                Decline
              </button>
              <button
                onClick={() => respondToEvent(event.id, 'accepted')}
                className="flex-1 rounded-xl bg-emerald-600 py-2 text-xs font-bold text-white"
              >
                Accept
              </button>
            </div>
          )}
        </div>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        {canEdit && (
          <Sheet open={showEdit} onClose={() => setShowEdit(false)} title="Edit event">
            <EditEventForm event={event} onClose={() => setShowEdit(false)} />
          </Sheet>
        )}

        <Sheet open={showFindCover} onClose={() => setShowFindCover(false)} title="Who can cover this shift?">
          <CoverPanel event={event} onDone={() => setShowFindCover(false)} />
        </Sheet>

        <Sheet open={showWithdraw} onClose={() => setShowWithdraw(false)} title="I can't do this shift">
          <WithdrawShiftForm event={event} onClose={() => setShowWithdraw(false)} />
        </Sheet>
      </div>
    </div>
  )
}

/**
 * Stepping back from an accepted shift. The shift is kept and handed back to
 * the participant as Needs Cover — it is never deleted, and no replacement is
 * chosen automatically.
 */
function WithdrawShiftForm({ event, onClose }: { event: ScheduleEvent; onClose: () => void }) {
  const { withdrawFromShift, allUsers, participantSpace } = useApp()
  const [reason, setReason] = useState('')
  const owner = allUsers.find((u) => u.id === participantSpace.participantUserId)

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-2xl bg-slate-50 p-3.5">
        <p className="text-sm font-bold text-slate-900">{event.title}</p>
        <p className="text-xs font-semibold text-slate-600">
          {new Date(event.date + 'T00:00:00').toLocaleDateString(undefined, {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}{' '}
          · {event.startTime} – {event.endTime}
        </p>
      </div>

      <p className="text-xs text-slate-600">
        The shift stays in the calendar and is marked <span className="font-bold">Needs Cover</span>.{' '}
        {owner ? `${owner.name.split(' ')[0]} and their nominee` : 'The participant and their nominee'} will be told
        straight away so they can ask someone else on the team.
      </p>

      <label className="block text-xs font-semibold text-slate-600">
        <span className="mb-1 block">Anything they should know? (optional)</span>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder="e.g. Unwell, sorry for the late notice."
          className="input"
        />
      </label>

      <div className="mt-1 flex gap-2">
        <button
          onClick={onClose}
          className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-600"
        >
          Keep the shift
        </button>
        <button
          onClick={() => {
            withdrawFromShift(event.id, reason)
            onClose()
          }}
          className="flex-1 rounded-xl bg-rose-600 py-3 text-sm font-bold text-white"
        >
          Withdraw from shift
        </button>
      </div>
    </div>
  )
}

function EditEventForm({ event, onClose }: { event: ScheduleEvent; onClose: () => void }) {
  const { currentUser, allUsers, updateEvent, deleteEvent } = useApp()
  const showAdultTiming = (currentUser.id === 'u-alex' || currentUser.id === 'u-mark') && !!event.calendarCategory
  const familyOnly = showAdultTiming && event.calendarAudience === 'family'
  const assignable = allUsers.filter((u) => u.id !== currentUser.id && (!showAdultTiming || !['u-leo', 'u-lillian', 'u-casey', 'u-mia', 'u-jordan', 'u-olivia', 'u-jnr'].includes(u.id)))

  const [title, setTitle] = useState(event.title)
  const [type, setType] = useState<EventType>(event.type)
  const [date, setDate] = useState(event.date)
  const [startTime, setStartTime] = useState(event.startTime)
  const [endTime, setEndTime] = useState(event.endTime)
  const [calendarTiming, setCalendarTiming] = useState(event.calendarTiming ?? 'timed')
  const [assigneeIds, setAssigneeIds] = useState<string[]>(event.assignees.map((a) => a.userId))
  const [notes, setNotes] = useState(event.notes ?? '')
  const [confirmDelete, setConfirmDelete] = useState(false)

  const toggleAssignee = (userId: string) => {
    setAssigneeIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
  }

  const save = () => {
    if (!title.trim()) return
    if (type === 'shift' && assigneeIds.length !== 1) return
    updateEvent(event.id, {
      title: title.trim(),
      type,
      date,
      startTime: showAdultTiming && calendarTiming !== 'timed' ? '00:00' : startTime,
      endTime: showAdultTiming && calendarTiming !== 'timed' ? calendarTiming === 'all-day' ? '23:59' : '00:00' : endTime,
      ...(showAdultTiming ? { calendarTiming } : {}),
      notes: notes.trim() || undefined,
      assigneeIds: familyOnly ? [] : assigneeIds,
    })
    onClose()
  }

  const remove = () => {
    deleteEvent(event.id)
    onClose()
  }

  const assigneesChanged =
    assigneeIds.length !== event.assignees.length ||
    !assigneeIds.every((id) => event.assignees.some((a) => a.userId === id))

  return (
    <div className="flex flex-col gap-3">
      <Field label="Title">
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="input" />
      </Field>

      {showAdultTiming ? <p className="text-xs font-semibold text-slate-600">Event type: <span className="font-bold text-slate-800">{event.calendarCategory}</span></p> : <Field label="Type">
        <div className="flex gap-2">
          {(Object.keys(typeMeta) as EventType[]).map((t) => (
            <button
              key={t}
              onClick={() => {
                setType(t)
                if (t === 'shift' && assigneeIds.length !== 1) {
                  setAssigneeIds(assigneeIds.slice(0, 1))
                }
              }}
              className={`flex-1 rounded-xl border py-2 text-xs font-semibold ${
                type === t ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-200 text-slate-500'
              }`}
            >
              {typeMeta[t].icon} {typeMeta[t].label}
            </button>
          ))}
        </div>
      </Field>}

      <Field label="Date">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input" />
      </Field>
      {showAdultTiming && <div className="flex flex-wrap gap-2">{([{ value: 'timed', label: 'Start and finish' }, { value: 'all-day', label: 'All day' }, { value: 'no-time', label: 'No time' }] as const).map(option => <label key={option.value} className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-2.5 py-2 text-xs font-bold text-slate-700"><input type="radio" name={`event-timing-${event.id}`} checked={calendarTiming === option.value} onChange={() => setCalendarTiming(option.value)} />{option.label}</label>)}</div>}
      {(!showAdultTiming || calendarTiming === 'timed') && <div className="flex gap-3">
        <Field label="Start" className="flex-1">
          <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input" />
        </Field>
        <Field label="End" className="flex-1">
          <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="input" />
        </Field>
      </div>}

      {!familyOnly && <div>
        <p className="mb-1 text-xs font-semibold text-slate-600">
          {type === 'shift' ? 'Assign to' : 'Assign to (optional, pick any number)'}
        </p>
        {type === 'shift' ? (
          <select
            value={assigneeIds[0] ?? ''}
            onChange={(e) => setAssigneeIds(e.target.value ? [e.target.value] : [])}
            className="input"
          >
            {assigneeIds.length === 0 && <option value="">— Choose who covers this shift —</option>}
            {assignable.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} — {u.title ?? u.role}
              </option>
            ))}
          </select>
        ) : (
          <div className="flex flex-col gap-1.5">
            {assignable.map((u) => (
              <label key={u.id} className="flex items-center gap-2 rounded-lg px-1 py-1 text-sm text-slate-700">
                <input type="checkbox" checked={assigneeIds.includes(u.id)} onChange={() => toggleAssignee(u.id)} />
                {u.name} <span className="text-xs text-slate-400">— {u.title ?? u.role}</span>
              </label>
            ))}
          </div>
        )}
        {assigneesChanged && (
          <p className="mt-1 text-[11px] text-amber-600">
            Changing who's assigned resets their status to "awaiting response" and notifies them.
          </p>
        )}
        {type === 'shift' && assigneeIds.length === 0 && (
          <p className="mt-1 text-[11px] text-rose-600">A shift needs exactly one person assigned.</p>
        )}
      </div>}

      <Field label="Notes (optional)">
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="input" />
      </Field>

      <div className="mt-1 flex gap-2">
        <button onClick={onClose} className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-600">
          Cancel
        </button>
        <button onClick={save} className="flex-1 rounded-xl bg-brand-600 py-3 text-sm font-bold text-white">
          Save Changes
        </button>
      </div>

      <div className="mt-2 border-t border-slate-100 pt-3">
        {confirmDelete ? (
          <div className="flex items-center gap-2">
            <p className="flex-1 text-xs text-slate-500">Delete this event? This can't be undone.</p>
            <button onClick={() => setConfirmDelete(false)} className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600">
              Cancel
            </button>
            <button onClick={remove} className="rounded-xl bg-rose-600 px-3 py-2 text-xs font-bold text-white">
              Delete
            </button>
          </div>
        ) : (
          <button onClick={() => setConfirmDelete(true)} className="w-full text-center text-xs font-semibold text-rose-600">
            Delete event
          </button>
        )}
      </div>
    </div>
  )
}

function Field({ label, children, className = '' }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block text-xs font-semibold text-slate-600 ${className}`}>
      <span className="mb-1 block">{label}</span>
      {children}
    </label>
  )
}

